import React from 'react';
import type { monaco } from 'react-monaco-editor';
import { remToPx } from 'Helpers/utils';
import { useTheme } from '@mui/material';
import { EditorProps } from '../EditorV3';

type UseEditorConfigProps = Pick<
  EditorProps,
  | 'ariaLabel'
  | 'codeLens'
  | 'readOnly'
  | 'fontSize'
  | 'hideLineNumbers'
  | 'wordBasedSuggestions'
  | 'hideScrollbar'
> & {
  hasLineErrors?: boolean;
};

const useEditorConfigV3 = ({
  readOnly,
  ariaLabel,
  codeLens = false,
  fontSize = 'base',
  hideLineNumbers = false,
  hasLineErrors = false,
  wordBasedSuggestions = true,
  hideScrollbar = false,
}: UseEditorConfigProps) => {
  const theme = useTheme();
  const { typographyTokens } = theme.palette;

  return React.useMemo<monaco.editor.IStandaloneEditorConstructionOptions>(
    () => ({
      codeLens,
      cursorBlinking: 'smooth',
      padding: {
        top: 8,
        bottom: 8,
      },
      dragAndDrop: true,
      lineNumbers: hideLineNumbers ? 'off' : 'on',
      folding: !hideLineNumbers,
      lineNumbersMinChars: 3,
      lineDecorationsWidth: 10,
      revealHorizontalRightPadding: 0,
      glyphMargin: hasLineErrors,
      ariaLabel,
      readOnly,
      domReadOnly: readOnly,
      renderLineHighlight: 'gutter',
      fontFamily: typographyTokens.fontFamilies.mono,
      fontWeight: `${typographyTokens.fontWeights.regular}`,
      fontSize: remToPx(typographyTokens.fontSizes[fontSize]),
      scrollBeyondLastLine: false,
      wordBasedSuggestions: wordBasedSuggestions ? 'currentDocument' : 'off',
      scrollbar: {
        alwaysConsumeMouseWheel: false,
        vertical: hideScrollbar ? 'hidden' : 'auto',
        horizontal: hideScrollbar ? 'hidden' : 'auto',
      },
      automaticLayout: true,
      wordWrap: 'on',
      quickSuggestions: true,
      renderIndentGuides: false,
      minimap: {
        enabled: false,
      },
      suggest: {
        insertMode: 'replace',
        localityBonus: true,
        showStatusBar: true,
      },
      inlineSuggest: {
        enabled: true,
        mode: 'subwordSmart',
        showToolbar: 'onHover',
      },
      inlayHints: {
        padding: true,
        fontFamily: typographyTokens.fontFamilies.sans,
        fontSize: remToPx(typographyTokens.fontSizes[fontSize]) - 1,
      },
      overviewRulerLanes: 0,
    }),
    // FIXME: look into hook dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, readOnly, hideLineNumbers, codeLens],
  );
};

export default useEditorConfigV3;
